import { iconNavAdicionar1, iconSetaDuplaEsquerda } from '@bsds-lib-component/icons/icons';
import { BsdsIcon, BsdsInput } from '@bsds-lib-component/react';
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import {
  Button,
  BeneficiariosTable,
  InclusionProponentesTable,
  SubEstipulanteInfoSection,
  SubestipulantesListSection,
  SystemMessage,
  VidaTableListToolbar,
  Visibility,
} from '../../../../components';
import {
  getInclusionProponentErrorRoute,
  useInclusionProponent,
  useSubEstipulanteInfo,
  useVidaTableListControls,
} from '../../../../hooks';
import { useSystemMessage } from '../../../../hooks/useSystemMessage';
import { MessageService } from '../../../../services/messageService';
import type { InclusionProponenteTableItem } from '../../../../types/vida/table/inclusionProponentesTableType';
import type { ListaSubestipulanteItem } from '../../../../types/vida/subEstipulante/subEstipulanteType';
import type { ProponentFormValues } from '../../../../types/vida/cadastralMaintenance/proponentFormTypes';
import type { PersonFormSection } from '../../../../types/vida/cadastralMaintenance/personFormTypes';
import { extractBsdsInputValue } from '../../../../utils/bsdsInputUtils';
import {
  sortProponentTableRows,
  filterProponentTableRows,
  filterProponentTableBySearch,
  EMPTY_PROPONENTES_PANEL_FILTERS,
} from '../../../../utils/inclusionProponentUtil';
import { PROPONENTES_SORT_OPTIONS } from '../../../../components/table/vida/InclusionProponentesTable';
import ImportProponentesBatchSection from './ImportProponentesBatchSection';
import NewProponentForm from './NewProponentForm';
import ProponentFormSection from './ProponentFormSection';
import ProponentInconsistencyModal from '../../../../components/pagesComponents/vidaPageComponents/modal/ProponentInconsistencyModal';
import PersonFormActionButtons from '../../../../components/pagesComponents/vidaPageComponents/PersonFormActionButtons';
import ProponentBeneficiarioFormSection from '../../../../components/pagesComponents/vidaPageComponents/ProponentBeneficiarioFormSection';
import type {
  BeneficiaryInclusionItem,
  BeneficiaryUpdateItem,
  ProponentDataReturn,
} from '../../../../types/vida/inclusionProponent/inclusionProponentType';
import type { ProponentBeneficiarioFormValues } from '../../../../types/vida/cadastralMaintenance/beneficiarioFormTypes';
import type { BeneficiarioTableItem } from '../../../../types/vida/table/beneficiariosTableType';
import { usePersonFormWizard } from '../../../../hooks/vida/usePersonFormWizard';
import {
  isProponentInconsistencyError,
  parseProponentInconsistenciesFromError,
} from '../../../../utils/inclusionProponentUtil';
import {
  createEmptyProponentBeneficiarioFormValues,
  mapProponentBeneficiarioFormToUpdateItem,
} from '../../../../utils/beneficiarioFormUtils';
import { resolveParentescoLabel } from '../../../../utils/parentescoUtils';

const TAB_LABEL = 'Movimentação Cadastral do Proponente';

type BeneficiariosScreen = 'list' | 'detail' | null;
type ProponentEditStep = PersonFormSection | 'beneficiarios';

const mapProponentBeneficiariosToTableRows = (
  beneficiarios: ProponentBeneficiarioFormValues[],
): BeneficiarioTableItem[] => beneficiarios.map((item, index) => ({
  id: item.idBeneficiario || `proponente-benef-${index}`,
  nomeBeneficiario: item.nmBeneficiario.trim() || '-',
  codTipoParentesco: item.cdParentesco.trim() || '-',
  parentesco: resolveParentescoLabel(item.cdParentesco),
  percentual: Number(item.pcParticipacao.replace(',', '.')) || 0,
  codBneficiario: item.idBeneficiario || null,
  source: {
    nomeBeneficiario: item.nmBeneficiario,
    codTipoParentesco: item.cdParentesco,
    percentualPart: Number(item.pcParticipacao.replace(',', '.')) || 0,
  },
}));

const resolveInclusionProponentDisplayError = (
  error: unknown,
  route: string,
  statusCode = 500,
): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim()) {
    return error;
  }
  return MessageService.getMappedErrorMessage(route, statusCode) ?? 'Erro na requisição';
};

const InclusionProponentesTab: React.FC = () => {
  const PAGE_SIZE = 10;
  const mainContentId = useId();
  const messageId = useId();
  const [currentPage, setCurrentPage] = useState(1);
  const [showImportProponentes, setShowImportProponentes] = useState(false);
  const [showAddProponente, setShowAddProponente] = useState(false);
  const [showAddActions, setShowAddActions] = useState(false);
  const [selectedProponent, setSelectedProponent] = useState<InclusionProponenteTableItem | null>(null);
  const [isEditingProponent, setIsEditingProponent] = useState(false);
  const [isProponentFormValid, setIsProponentFormValid] = useState(false);
  const [currentFormValues, setCurrentFormValues] = useState<ProponentFormValues | null>(null);
  const [formResetKey, setFormResetKey] = useState(0);
  const [isSaveInFlight, setIsSaveInFlight] = useState(false);
  const [isInconsistencyModalOpen, setIsInconsistencyModalOpen] = useState(false);
  const [inconsistencies, setInconsistencies] = useState<ProponentDataReturn[]>([]);
  const [beneficiarios, setBeneficiarios] = useState<ProponentBeneficiarioFormValues[]>([]);
  const [beneficiariosScreen, setBeneficiariosScreen] = useState<BeneficiariosScreen>(null);
  const [selectedBeneficiarioIndex, setSelectedBeneficiarioIndex] = useState<number | null>(null);
  const [beneficiarioDraft, setBeneficiarioDraft] = useState<ProponentBeneficiarioFormValues | null>(null);
  const [isBeneficiarioValid, setIsBeneficiarioValid] = useState(false);
  const [editFormSection, setEditFormSection] = useState<PersonFormSection>('personData');
  const editSessionBeneficiariosRef = useRef<ProponentBeneficiarioFormValues[] | null>(null);
  const { setTabBreadcrumbSuffix, setDetailBreadcrumbSuffix } = useBreadcrumb();

  const editWizardFormKey = `${selectedProponent?.identProponente ?? 'none'}-${formResetKey}-${isEditingProponent ? 'edit' : 'view'}`;
  const {
    sectionControlRef: editSectionControlRef,
    onActiveSectionChange: onEditWizardSectionChange,
  } = usePersonFormWizard(editWizardFormKey);

  const onEditActiveSectionChange = useCallback((section: PersonFormSection) => {
    setEditFormSection(section);
    onEditWizardSectionChange(section);
  }, [onEditWizardSectionChange]);

  useEffect(() => {
    setEditFormSection('personData');
  }, [editWizardFormKey]);

  const sub = useSubEstipulanteInfo();
  const proponent = useInclusionProponent();
  const { showMessage, message, type, show, hideMessage } = useSystemMessage();

  const listControls = useVidaTableListControls({
    emptyFilters: EMPTY_PROPONENTES_PANEL_FILTERS,
    loadedRowCount: proponent.list.rows.length,
    debounceMs: 0,
    resetPanelFiltersDeps: [sub.list.identSubestipulante],
  });

  const {
    listSearchInputValue,
    sortBy,
    appliedPanelFilters,
    panelFilters,
    updatePanelFilter,
    resetAll: resetListControls,
    isPanelFilterActive,
  } = listControls;

  const isSubmitting = isSaveInFlight || proponent.detail.isAdding || proponent.detail.isSaving;
  const isBeneficiariosListActive = beneficiariosScreen === 'list';
  const isBeneficiariosDetailActive = beneficiariosScreen === 'detail';
  const beneficiariosRows = useMemo(
    () => mapProponentBeneficiariosToTableRows(beneficiarios),
    [beneficiarios],
  );

  const currentEditStep: ProponentEditStep = (isBeneficiariosListActive || isBeneficiariosDetailActive)
    ? 'beneficiarios'
    : editFormSection;

  const isEditWizardLastSection = !isBeneficiariosDetailActive
    && currentEditStep === 'beneficiarios';

  const resetBeneficiariosScreen = useCallback((options?: { clearDraft?: boolean }) => {
    setBeneficiariosScreen(null);
    if (options?.clearDraft !== false) {
      setSelectedBeneficiarioIndex(null);
      setBeneficiarioDraft(null);
      setIsBeneficiarioValid(false);
    }
  }, []);

  const resetBeneficiariosState = useCallback(() => {
    setBeneficiarios([]);
    resetBeneficiariosScreen();
  }, [resetBeneficiariosScreen]);

  const beginEditSession = useCallback(() => {
    if (!isEditingProponent) {
      editSessionBeneficiariosRef.current = beneficiarios.map((item) => ({ ...item }));
    }
    setIsEditingProponent(true);
  }, [isEditingProponent, beneficiarios]);

  const cancelEditSession = useCallback(() => {
    if (editSessionBeneficiariosRef.current) {
      setBeneficiarios(editSessionBeneficiariosRef.current);
    }
    editSessionBeneficiariosRef.current = null;
    setCurrentFormValues(null);
    setIsEditingProponent(false);
    resetBeneficiariosScreen();
    setFormResetKey((key) => key + 1);
  }, [resetBeneficiariosScreen]);

  const showProponentDetail = selectedProponent !== null;
  const showSubScreen = showImportProponentes
    || showAddProponente
    || sub.list.showSubestipulantePanel
    || showProponentDetail;

  const screenTitle = useMemo(() => {
    if (sub.list.subestipulantePanelView === 'info') {
      return 'Informações Cadastrais';
    }
    if (showImportProponentes) {
      return 'Importar Proponentes em Lote';
    }
    if (showAddProponente) {
      return 'Adicionar Proponente';
    }
    if (selectedProponent) {
      if (isBeneficiariosDetailActive && beneficiarioDraft) {
        return `Beneficiário — ${beneficiarioDraft.nmBeneficiario || 'Novo'}`;
      }
      if (isBeneficiariosListActive) {
        return `Beneficiários — ${selectedProponent.nome}`;
      }
      if (isEditingProponent) {
        return `Editar Proponente — ${selectedProponent.nome}`;
      }
      return `Visualizar Proponente — ${selectedProponent.nome}`;
    }
    return TAB_LABEL;
  }, [
    sub.list.subestipulantePanelView,
    showImportProponentes,
    showAddProponente,
    selectedProponent,
    isEditingProponent,
    isBeneficiariosListActive,
    isBeneficiariosDetailActive,
    beneficiarioDraft,
  ]);

  const handleOpenImportProponentes = (subestipulante?: ListaSubestipulanteItem) => {
    if (subestipulante) {
      sub.list.setIdentSubestipulante(String(subestipulante.identSubestipulanteOut));
    }
    setShowAddActions(false);
    setShowAddProponente(false);
    setShowImportProponentes(true);
  };

  const handleCancelImportProponentes = () => {
    setShowImportProponentes(false);
  };

  const handleOpenAddProponente = (subestipulante?: ListaSubestipulanteItem) => {
    if (subestipulante) {
      sub.list.setIdentSubestipulante(String(subestipulante.identSubestipulanteOut));
    }
    setSelectedProponent(null);
    proponent.detail.reset();
    setFormResetKey((key) => key + 1);
    setShowAddActions(false);
    setShowImportProponentes(false);
    setShowAddProponente(true);
  };

  const handleCancelAddProponente = () => {
    if (isSubmitting) {
      return;
    }
    setShowAddProponente(false);
    setFormResetKey((key) => key + 1);
  };

  const resetInconsistencyModal = useCallback(() => {
    setIsInconsistencyModalOpen(false);
    setInconsistencies([]);
  }, []);

  const handleOpenProponentDetail = (row: InclusionProponenteTableItem) => {
    resetInconsistencyModal();
    setShowImportProponentes(false);
    setIsEditingProponent(false);
    editSessionBeneficiariosRef.current = null;
    setCurrentFormValues(null);
    resetBeneficiariosState();
    setFormResetKey((key) => key + 1);
    setSelectedProponent(row);
    void proponent.detail.loadProponentData({
      identSubestipulante: row.identSubestipulante,
      identProponente: row.identProponente,
    });
  };

  const handleBackFromProponentDetail = () => {
    resetInconsistencyModal();
    if (isBeneficiariosDetailActive) {
      if (isEditingProponent && beneficiarioDraft && isBeneficiarioValid) {
        handleSaveBeneficiario();
        return;
      }
      setBeneficiariosScreen('list');
      if (!isEditingProponent) {
        setSelectedBeneficiarioIndex(null);
        setBeneficiarioDraft(null);
        setIsBeneficiarioValid(false);
      }
      return;
    }
    if (isBeneficiariosListActive) {
      resetBeneficiariosScreen({ clearDraft: !isEditingProponent });
      return;
    }
    if (isEditingProponent) {
      cancelEditSession();
      return;
    }
    resetBeneficiariosState();
    setSelectedProponent(null);
    proponent.detail.reset();
  };

  const handleOpenBeneficiarios = () => {
    setBeneficiariosScreen('list');
    if (!isEditingProponent) {
      setSelectedBeneficiarioIndex(null);
      setBeneficiarioDraft(null);
      setIsBeneficiarioValid(false);
    }
  };

  const handleAddBeneficiario = () => {
    if (beneficiarioDraft && selectedBeneficiarioIndex == null) {
      setBeneficiariosScreen('detail');
      return;
    }
    setSelectedBeneficiarioIndex(null);
    setBeneficiarioDraft(createEmptyProponentBeneficiarioFormValues(
      currentFormValues?.matricula ?? selectedProponent?.matricula ?? '',
      String(beneficiarios.length + 1),
    ));
    setBeneficiariosScreen('detail');
  };

  const handleEditBeneficiario = (row: BeneficiarioTableItem) => {
    const index = beneficiariosRows.findIndex((item) => item.id === row.id);
    if (index < 0) {
      return;
    }
    const keepDraft = selectedBeneficiarioIndex === index && beneficiarioDraft
      ? beneficiarioDraft
      : beneficiarios[index];
    setSelectedBeneficiarioIndex(index);
    setBeneficiarioDraft(keepDraft);
    setBeneficiariosScreen('detail');
  };

  const handleSaveBeneficiario = () => {
    if (!beneficiarioDraft || !isBeneficiarioValid) {
      return;
    }

    setBeneficiarios((current) => {
      if (selectedBeneficiarioIndex == null) {
        return [...current, beneficiarioDraft];
      }
      return current.map((item, index) => (
        index === selectedBeneficiarioIndex ? beneficiarioDraft : item
      ));
    });
    setBeneficiariosScreen('list');
    setSelectedBeneficiarioIndex(null);
    setBeneficiarioDraft(null);
  };

  const handleSaveProponent = async () => {
    if (!currentFormValues) {
      return;
    }

    setIsSaveInFlight(true);
    try {
      const beneficiariosPayload: BeneficiaryUpdateItem[] = beneficiarios.map(
        (item, index) => mapProponentBeneficiarioFormToUpdateItem(item, index),
      );
      await proponent.detail.updateProponentData(currentFormValues, beneficiariosPayload);
      resetInconsistencyModal();
      editSessionBeneficiariosRef.current = null;
      resetBeneficiariosState();
      setIsEditingProponent(false);
      setSelectedProponent(null);
      proponent.detail.reset();
      await reloadProponentList();
      showMessage('Proponente atualizado com sucesso.', 'success');
    } catch (err) {
      const parsedInconsistencies = isProponentInconsistencyError(err)
        ? err.inconsistencies
        : parseProponentInconsistenciesFromError(err);

      if (parsedInconsistencies.length > 0) {
        setInconsistencies(parsedInconsistencies);
        setIsInconsistencyModalOpen(true);
        return;
      }
      // error state handled by hook
    } finally {
      setIsSaveInFlight(false);
    }
  };

  const handleEditWizardContinueOrSave = () => {
    if (isEditWizardLastSection) {
      void handleSaveProponent();
      return;
    }

    if (currentEditStep === 'beneficiarios') {
      if (isBeneficiariosDetailActive) {
        if (beneficiarioDraft && isBeneficiarioValid) {
          handleSaveBeneficiario();
        } else {
          setBeneficiariosScreen('list');
        }
        return;
      }
      return;
    }

    if (currentEditStep === 'insuranceInfo') {
      handleOpenBeneficiarios();
      return;
    }

    editSectionControlRef.current?.goToNextSection();
  };

  const handleBreadcrumbBackToView = useCallback(() => {
    if (isEditingProponent) {
      cancelEditSession();
      return;
    }
    resetBeneficiariosScreen();
    setFormResetKey((key) => key + 1);
  }, [cancelEditSession, isEditingProponent, resetBeneficiariosScreen]);

  const handleViewProponentes = (subestipulante: ListaSubestipulanteItem) => {
    setShowImportProponentes(false);
    setShowAddProponente(false);
    setShowAddActions(false);
    setSelectedProponent(null);
    setIsEditingProponent(false);
    resetBeneficiariosState();
    proponent.detail.reset();
    resetListControls();
    setCurrentPage(1);
    proponent.list.reset();
    sub.resumo.close();
    sub.list.handleViewSubestipulante(subestipulante);
  };

  const handleInfoSubEstipulante = (subestipulante: ListaSubestipulanteItem) => {
    setShowImportProponentes(false);
    setShowAddProponente(false);
    setShowAddActions(false);
    setSelectedProponent(null);
    setIsEditingProponent(false);
    resetBeneficiariosState();
    proponent.detail.reset();
    proponent.list.reset();
    sub.resumo.close();
    sub.list.handleInfoSubEstipulante(subestipulante);
  };

  const handleBackToSubestipulantesList = () => {
    resetListControls();
    setCurrentPage(1);
    setShowAddActions(false);
    setSelectedProponent(null);
    setIsEditingProponent(false);
    resetBeneficiariosState();
    proponent.detail.reset();
    proponent.list.reset();
    sub.resumo.close();
    sub.list.handleBackToSubestipulantesList();
  };

  const showBackToSubestipulantes = sub.list.subestipulantePanelView === 'info'
    || (!showSubScreen && sub.list.hasSearched);

  const handleBackToSubestipulantes = () => {
    if (sub.list.subestipulantePanelView === 'info') {
      sub.list.handleBackFromSubestipulantePanel();
      return;
    }
    handleBackToSubestipulantesList();
  };

  const handleBreadcrumbBackToList = useCallback(() => {
    setShowImportProponentes(false);
    setShowAddProponente(false);
    setShowAddActions(false);
    setSelectedProponent(null);
    setIsEditingProponent(false);
    resetBeneficiariosState();
    proponent.detail.reset();
    proponent.list.reset();
    sub.resumo.close();
    sub.list.resetSubestipulantePanel();
  }, [
    proponent.detail.reset,
    proponent.list.reset,
    resetBeneficiariosState,
    sub.list.resetSubestipulantePanel,
    sub.resumo.close,
  ]);

  useEffect(() => {
    const hasSubScreen = showImportProponentes
      || showAddProponente
      || sub.list.showSubestipulantePanel
      || showProponentDetail;

    setTabBreadcrumbSuffix([
      {
        label: TAB_LABEL,
        ...(hasSubScreen ? { onClick: handleBreadcrumbBackToList } : {}),
      },
    ]);

    if (sub.list.subestipulantePanelView === 'info') {
      setDetailBreadcrumbSuffix([{ label: 'Informações Cadastrais' }]);
      return;
    }

    if (showImportProponentes) {
      setDetailBreadcrumbSuffix([{ label: 'Importar Proponentes em Lote' }]);
      return;
    }
    if (showAddProponente) {
      setDetailBreadcrumbSuffix([{ label: 'Adicionar Proponente' }]);
      return;
    }

    if (selectedProponent) {
      if (isBeneficiariosDetailActive && beneficiarioDraft) {
        setDetailBreadcrumbSuffix([
          { label: selectedProponent.nome, onClick: handleBreadcrumbBackToView },
          { label: 'Beneficiários', onClick: () => setBeneficiariosScreen('list') },
          { label: beneficiarioDraft.nmBeneficiario || 'Novo beneficiário' },
        ]);
        return;
      }
      if (isBeneficiariosListActive) {
        setDetailBreadcrumbSuffix([
          { label: selectedProponent.nome, onClick: handleBreadcrumbBackToView },
          { label: 'Beneficiários' },
        ]);
        return;
      }
      if (isEditingProponent) {
        setDetailBreadcrumbSuffix([
          { label: selectedProponent.nome, onClick: handleBreadcrumbBackToView },
          { label: 'Editar Proponente' },
        ]);
        return;
      }
      setDetailBreadcrumbSuffix([{ label: selectedProponent.nome }]);
      return;
    }

    setDetailBreadcrumbSuffix([]);
  }, [
    sub.list.subestipulantePanelView,
    sub.list.showSubestipulantePanel,
    showImportProponentes,
    showAddProponente,
    showProponentDetail,
    selectedProponent,
    isEditingProponent,
    isBeneficiariosListActive,
    isBeneficiariosDetailActive,
    beneficiarioDraft,
    setTabBreadcrumbSuffix,
    setDetailBreadcrumbSuffix,
    handleBreadcrumbBackToList,
    handleBreadcrumbBackToView,
  ]);

  useEffect(() => () => {
    setDetailBreadcrumbSuffix([]);
  }, [setDetailBreadcrumbSuffix]);

  useEffect(() => {
    if (proponent.detail.error) {
      showMessage(
        resolveInclusionProponentDisplayError(
          proponent.detail.error,
          getInclusionProponentErrorRoute('detail'),
        ),
        'error',
      );
      return;
    }

    if (proponent.list.error) {
      showMessage(
        resolveInclusionProponentDisplayError(
          proponent.list.error,
          getInclusionProponentErrorRoute('list'),
        ),
        'error',
      );
      return;
    }

    hideMessage();
  }, [proponent.detail.error, proponent.list.error, showMessage, hideMessage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [listSearchInputValue, sortBy, sub.list.identSubestipulante]);

  useEffect(() => {
    if (!sub.list.hasSearched || !sub.list.identSubestipulante) {
      proponent.list.reset();
      return;
    }

    void proponent.list.loadProponentList(Number(sub.list.identSubestipulante), {
      page: currentPage - 1,
      size: PAGE_SIZE,
    });
  }, [
    sub.list.hasSearched,
    sub.list.identSubestipulante,
    currentPage,
    proponent.list.loadProponentList,
    proponent.list.reset,
  ]);

  const filteredProponentesRows = useMemo(() => {
    const rowsBySearch = filterProponentTableBySearch(proponent.list.rows, listSearchInputValue);
    return filterProponentTableRows(rowsBySearch, appliedPanelFilters);
  }, [proponent.list.rows, listSearchInputValue, appliedPanelFilters]);

  const sortedProponentesRows = useMemo(
    () => sortProponentTableRows(filteredProponentesRows, sortBy),
    [filteredProponentesRows, sortBy],
  );

  const isListFilterActive = listSearchInputValue.trim() !== '' || isPanelFilterActive;

  const totalItems = proponent.list.totalElements;
  const totalPages = Math.max(1, proponent.list.totalPages);
  const tableCurrentPage = isListFilterActive ? 1 : currentPage;
  const tableTotalPages = isListFilterActive ? 1 : totalPages;
  const tableTotalItems = isListFilterActive ? sortedProponentesRows.length : totalItems;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page: number) => {
    if (isListFilterActive) {
      return;
    }
    setCurrentPage(page);
  };

  const reloadProponentList = useCallback(async () => {
    if (!sub.list.identSubestipulante) {
      return;
    }

    await proponent.list.loadProponentList(Number(sub.list.identSubestipulante), {
      page: currentPage - 1,
      size: PAGE_SIZE,
    });
  }, [
    sub.list.identSubestipulante,
    currentPage,
    proponent.list.loadProponentList,
  ]);

  const handleDeleteSelectedProponents = async (rows: InclusionProponenteTableItem[]) => {
    if (rows.length === 0) {
      return;
    }

    try {
      await proponent.detail.inactivateProponents(rows.map((row) => row.identProponente));
      await reloadProponentList();
      showMessage('Proponente Excluído com sucesso.', 'success');
    } catch {
      // error state handled by hook
    }
  };

  const handleSaveNewProponent = async (
    values: ProponentFormValues,
    beneficiariosPayload: BeneficiaryInclusionItem[],
  ) => {
    const identSubestipulante = sub.list.identSubestipulante;
    const idContrato = sub.list.selectedSubestipulante?.identContratoOut;

    if (!identSubestipulante || idContrato == null) {
      return;
    }

    setIsSaveInFlight(true);
    try {
      await proponent.detail.addProponentData(
        Number(identSubestipulante),
        values,
        idContrato,
        beneficiariosPayload,
      );
      await reloadProponentList();
      resetInconsistencyModal();
      proponent.detail.reset();
      setShowAddProponente(false);
      setFormResetKey((key) => key + 1);
      showMessage('Proponente incluído com sucesso.', 'success');
    } catch (err) {
      const parsedInconsistencies = isProponentInconsistencyError(err)
        ? err.inconsistencies
        : parseProponentInconsistenciesFromError(err);

      if (parsedInconsistencies.length > 0) {
        setInconsistencies(parsedInconsistencies);
        setIsInconsistencyModalOpen(true);
        return;
      }

      showMessage(
        resolveInclusionProponentDisplayError(err, getInclusionProponentErrorRoute('add')),
        'error',
      );
    } finally {
      setIsSaveInFlight(false);
    }
  };

  return (
    <div
      className="bsds-width-100 bsds-mt-12 bsds-p-8"
      role="main"
      aria-label="Inclusão de proponentes"
      id={mainContentId}
    >
      {show && (
        <div
          id={messageId}
          className="bsds-mb-16"
          role={type === 'error' ? 'alert' : 'status'}
          aria-live={type === 'error' ? 'assertive' : 'polite'}
          aria-atomic="true"
        >
          <SystemMessage
            message={message}
            type={type}
            show={show}
          />
        </div>
      )}

      {showBackToSubestipulantes && (
        <button
          type="button"
          className="bsds-display-flex bsds-align-items-center cursor-pointer text-gray-700 bsds-mb-16 border-0 bg-transparent p-0 mb-8"
          onClick={handleBackToSubestipulantes}
          aria-label="Voltar para listagem de subestipulantes"
        >
          <BsdsIcon
            icon={iconSetaDuplaEsquerda}
            className="w-4 h-4 text-gray-700 shrink-0 bsds-mr-8"
            aria-hidden="true"
          />
          Voltar
        </button>
      )}

      <div className="bsds-display-flex flex-col md:flex-row bsds-justify-content-between md:bsds-align-items-center gap-3 bsds-mb-24">
        <div className="bsds-display-flex bsds-justify-content-start bsds-align-items-center">
          <div className="bsds-display-flex bsds-align-items-center">
            <div className="w-[2.5px] h-8  bg-red-600 bsds-mr-8" aria-hidden="true" />
          </div>
          <h6 className="bsds-ml-8 font-normal text-lg">
            {showSubScreen
              ? [
                  screenTitle,
                  sub.list.selectedSubestipulante?.nomeSubestipulanteOut?.trim(),
                ].filter(Boolean).join(' - ')
              : TAB_LABEL}
          </h6>
        </div>
        {!showSubScreen && sub.list.hasSearched && sub.list.identSubestipulante && (
          <div className="bsds-display-flex md:bsds-justify-content-end bsds-align-items-center min-h-10 relative">
            <Button
              type="button"
              color="primary"
              fill="solid"
              icon={iconNavAdicionar1}
              iconPosition="start"
              onClick={() => setShowAddActions((open) => !open)}
              aria-label="Nova inclusão"
            >
              Nova Inclusão
            </Button>
            {showAddActions && (
              <div
                className="absolute right-0 top-12 z-10 bg-white border border-gray-200 rounded-md shadow-md min-w-[220px] py-2"
                role="menu"
                aria-label="Ações de inclusão de proponentes"
              >
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                  onClick={() => handleOpenAddProponente(sub.list.selectedSubestipulante ?? undefined)}
                  role="menuitem"
                >
                  Adicionar Proponente
                </button>
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                  onClick={() => handleOpenImportProponentes(sub.list.selectedSubestipulante ?? undefined)}
                  role="menuitem"
                >
                  Importar Proponentes
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <Visibility isActive={showAddProponente} animated animationDuration={300}>
        <NewProponentForm
          formKey={formResetKey}
          isSaving={isSubmitting}
          onSave={handleSaveNewProponent}
          onCancel={handleCancelAddProponente}
        />
      </Visibility>

      <Visibility isActive={showImportProponentes} animated animationDuration={300}>
        <ImportProponentesBatchSection
          onCancel={handleCancelImportProponentes}
          onImportSuccess={reloadProponentList}
          subestipulante={sub.list.selectedSubestipulante}
          proponentImport={proponent.import}
        />
      </Visibility>

      <Visibility isActive={showProponentDetail} animated animationDuration={300}>
        <div className="bsds-mt-24" role="region" aria-label="Dados do proponente" aria-busy={proponent.detail.isLoading || isSubmitting}>
          {proponent.detail.formValues && (
            <ProponentFormSection
              key={`${selectedProponent?.identProponente}-${formResetKey}`}
              initialValues={proponent.detail.formValues}
              readOnly={!isEditingProponent || isSubmitting}
              onValidityChange={setIsProponentFormValid}
              onValuesChange={setCurrentFormValues}
              showBeneficiariosButton
              isBeneficiariosActive={isBeneficiariosListActive || isBeneficiariosDetailActive}
              onBeneficiariosClick={handleOpenBeneficiarios}
              onExitBeneficiarios={() => resetBeneficiariosScreen({ clearDraft: !isEditingProponent })}
              sectionControlRef={editSectionControlRef}
              onActiveSectionChange={onEditActiveSectionChange}
            />
          )}

          {isBeneficiariosListActive && (
            <div className="bsds-mt-16" role="region" aria-label="Lista de beneficiários do proponente">
              <BeneficiariosTable
                rows={beneficiariosRows}
                onEdit={handleEditBeneficiario}
                onAdd={handleAddBeneficiario}
                canEdit={isEditingProponent}
              />
            </div>
          )}

          {isBeneficiariosDetailActive && beneficiarioDraft && (
            <div className="bsds-mt-16">
              <ProponentBeneficiarioFormSection
                key={`beneficiario-edit-${selectedBeneficiarioIndex ?? 'new'}-${formResetKey}`}
                initialValues={beneficiarioDraft}
                readOnly={!isEditingProponent || isSubmitting}
                onValidityChange={setIsBeneficiarioValid}
                onValuesChange={setBeneficiarioDraft}
              />
            </div>
          )}

          <div className="bsds-display-flex bsds-pt-16 flex-col sm:flex-row gap-4 mt-24">
            {isEditingProponent && (
              <PersonFormActionButtons
                isLastSection={isEditWizardLastSection}
                isSaving={isSubmitting}
                canSave={isProponentFormValid}
                onPrimaryClick={handleEditWizardContinueOrSave}
                onCancel={cancelEditSession}
                saveAriaLabel={`Salvar alterações de ${selectedProponent?.nome}`}
                continueAriaLabel={`Continuar edição de ${selectedProponent?.nome}`}
                cancelAriaLabel={`Cancelar edição de ${selectedProponent?.nome}`}
                cancelColor="primary"
              />
            )}
            {!isEditingProponent && !isBeneficiariosListActive && !isBeneficiariosDetailActive && (
              <>
                <Button
                  type="button"
                  color="primary"
                  fill="solid"
                  className="bsds-width-100 bsds-sm-width-50 order-1 sm:order-2"
                  loading={proponent.detail.isLoading}
                  onClick={() => beginEditSession()}
                  disabled={isSubmitting}
                  aria-label={`Editar cadastro de ${selectedProponent?.nome}`}
                  aria-busy={proponent.detail.isLoading}
                >
                  Editar
                </Button>
                <Button
                  type="button"
                  onClick={handleBackFromProponentDetail}
                  color="secondary"
                  fill="outline"
                  className="bsds-width-100 bsds-sm-width-50 order-2 sm:order-1"
                  aria-label="Voltar para lista de proponentes"
                >
                  Voltar
                </Button>
              </>
            )}
            {!isEditingProponent && (isBeneficiariosListActive || isBeneficiariosDetailActive) && (
              <>
                <Button
                  type="button"
                  color="primary"
                  fill="solid"
                  className="bsds-width-100 bsds-sm-width-50 order-1 sm:order-2"
                  loading={proponent.detail.isLoading}
                  onClick={() => beginEditSession()}
                  disabled={isSubmitting}
                  aria-label={`Editar cadastro de ${selectedProponent?.nome}`}
                  aria-busy={proponent.detail.isLoading}
                >
                  Editar
                </Button>
                <Button
                  type="button"
                  onClick={handleBackFromProponentDetail}
                  color="secondary"
                  fill="outline"
                  className="bsds-width-100 bsds-sm-width-50 order-2 sm:order-1"
                  aria-label="Voltar para dados do proponente"
                >
                  Voltar
                </Button>
              </>
            )}
          </div>
        </div>
      </Visibility>

      <Visibility isActive={sub.list.subestipulantePanelView === 'info'} animated animationDuration={300}>
        <SubEstipulanteInfoSection
          subestipulante={sub.list.selectedSubestipulante}
          info={sub.info}
        />
      </Visibility>

      <Visibility isActive={!showSubScreen} animated animationDuration={300}>
        <SubestipulantesListSection
          hasSearched={sub.list.hasSearched}
          selectedSubestipulante={sub.list.selectedSubestipulante}
          subestipulantesList={sub.list.subestipulantesList}
          numeroApolice={sub.list.numeroApoliceSubestipulantes}
          subestipulanteResumo={sub.resumo}
          viewAction="proponentes"
          onViewSubestipulante={handleViewProponentes}
          onInfoSubEstipulante={handleInfoSubEstipulante}
        />

        {sub.list.hasSearched && (
          <>
            <VidaTableListToolbar
              groupAriaLabel="Busca na lista e ordenacao de proponentes"
              searchPlaceholder="Filtre por matricula, CPF, nome ou status"
              searchAriaLabel="Filtre por matricula, CPF, nome ou status"
              sortId="sort-inclusion-proponentes"
              sortOptions={PROPONENTES_SORT_OPTIONS}
              filtersPanelAriaLabel="Configurações de filtros de proponentes"
              entityLabel="proponentes"
              controls={listControls}
              showSearchClearInput={false}
            >
              <div className="bsds-display-flex flex-col sm:flex-row gap-4">
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="Status"
                    value={panelFilters.status}
                    onInput={(e) => updatePanelFilter('status', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="CPF"
                    value={panelFilters.cpf}
                    onInput={(e) => updatePanelFilter('cpf', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
              </div>
              <div className="bsds-display-flex flex-col sm:flex-row gap-4">
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="Nome"
                    value={panelFilters.nome}
                    onInput={(e) => updatePanelFilter('nome', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="Sexo"
                    value={panelFilters.sexo}
                    onInput={(e) => updatePanelFilter('sexo', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
              </div>
              <div className="bsds-display-flex flex-col sm:flex-row gap-4">
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="Nascimento"
                    value={panelFilters.nascimento}
                    onInput={(e) => updatePanelFilter('nascimento', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="Matrícula"
                    value={panelFilters.matricula}
                    onInput={(e) => updatePanelFilter('matricula', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
              </div>
              <div className="bsds-display-flex flex-col sm:flex-row gap-4">
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="Cargo/Função"
                    value={panelFilters.cargoFuncao}
                    onInput={(e) => updatePanelFilter('cargoFuncao', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
              </div>
            </VidaTableListToolbar>

            <div
              className="bsds-mt-16"
              role="region"
              aria-label="Lista de proponentes"
              aria-busy={proponent.list.isLoading}
            >
              <InclusionProponentesTable
                rows={sortedProponentesRows}
                currentPage={tableCurrentPage}
                totalPages={tableTotalPages}
                totalItems={tableTotalItems}
                onPageChange={handlePageChange}
                onDeleteSelected={(rows) => void handleDeleteSelectedProponents(rows)}
                isDeleting={proponent.detail.isDeleting}
                onRowClick={handleOpenProponentDetail}
                emptyMessage={
                  proponent.list.isLoading
                    ? 'Carregando lista de Proponentes...'
                    : isListFilterActive
                      ? 'Nenhum Proponente encontrado para os filtros informados'
                      : 'Nenhum Proponente encontrado para a pesquisa informada'
                }
              />
            </div>
          </>
        )}
      </Visibility>

      <ProponentInconsistencyModal
        isOpen={isInconsistencyModalOpen}
        inconsistencies={inconsistencies}
        onClose={resetInconsistencyModal}
      />
    </div>
  );
};

export default InclusionProponentesTab;
